/**
 * Brand UI kit — a small, consistent set of primitives for the admin panel.
 *
 * Design language: minimal, precise, professional. Neutral gray surfaces,
 * a single blue accent, restrained color, tight (compact) spacing, clear
 * hierarchy. Shared by App Setup, Form Setup, Tenant Sidebar, etc.
 */
import { useEffect } from "react";
import { HiX } from "react-icons/hi";
import { FiSearch } from "react-icons/fi";

export const cn = (...parts) => parts.filter(Boolean).join(" ");

// ── Page shell ─────────────────────────────────────────────────────────────
export const Page = ({ children, className }) => (
  <div className="page-content bg-gray-50 min-h-screen">
    <div className={cn("w-full px-4 md:px-8 py-4", className)}>{children}</div>
  </div>
);

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
    <div className="min-w-0">
      <h1 className="text-lg font-bold text-gray-900 tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-gray-500 mt-1 max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
  </div>
);

// ── Buttons ────────────────────────────────────────────────────────────────
const BTN_BASE =
  "inline-flex items-center justify-center gap-1.5 font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap";
const BTN_SIZE = {
  xs: "px-2.5 py-1 text-xs",
  sm: "px-3 py-1.5 text-xs",
  md: "px-4 py-2 text-sm",
};
const BTN_VARIANT = {
  primary: "bg-blue-600 text-white hover:bg-blue-700 shadow-sm",
  outline: "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium",
  soft: "border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100",
  ghost: "text-gray-500 hover:text-gray-800 hover:bg-gray-100 font-medium",
  danger: "border border-red-200 bg-white text-red-600 hover:bg-red-50",
  dashed:
    "border border-dashed border-gray-300 bg-white text-gray-500 hover:border-blue-400 hover:text-blue-600 font-medium",
};
export const Button = ({ variant = "outline", size = "md", icon, className, children, ...props }) => (
  <button type="button" className={cn(BTN_BASE, BTN_SIZE[size], BTN_VARIANT[variant], className)} {...props}>
    {icon}
    {children}
  </button>
);

export const IconButton = ({ icon, tone = "gray", className, ...props }) => {
  const tones = {
    gray: "text-gray-400 hover:text-gray-700 hover:bg-gray-100",
    blue: "text-gray-400 hover:text-blue-600 hover:bg-blue-50",
    red: "text-gray-400 hover:text-red-600 hover:bg-red-50",
  };
  return (
    <button type="button" className={cn("p-1.5 rounded-lg transition-colors", tones[tone], className)} {...props}>
      {icon}
    </button>
  );
};

// ── Search ─────────────────────────────────────────────────────────────────
export const SearchInput = ({ value, onChange, placeholder = "Search…", className, fullWidth }) => (
  <div className={cn("relative", fullWidth ? "w-full" : "max-w-sm", className)}>
    <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
    <input
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="block w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all"
    />
  </div>
);

export const Input = ({ className, ...props }) => (
  <input
    className={cn(
      "block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all",
      className,
    )}
    {...props}
  />
);

export const Label = ({ children, className }) => (
  <label className={cn("block text-sm font-medium text-gray-700 mb-1", className)}>{children}</label>
);

// ── Surface ────────────────────────────────────────────────────────────────
export const Surface = ({ children, className }) => (
  <div className={cn("bg-white rounded-xl border border-gray-200 shadow-sm", className)}>{children}</div>
);

// ── Table primitives (compact) ─────────────────────────────────────────────
export const Table = ({ children, className }) => (
  <div className={cn("bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden", className)}>
    <table className="w-full text-sm">{children}</table>
  </div>
);
export const THead = ({ children }) => (
  <thead className="bg-gray-50 text-gray-500 border-b border-gray-200">{children}</thead>
);
export const Th = ({ children, className }) => (
  <th className={cn("text-left font-semibold text-[11px] uppercase tracking-wide px-4 py-2.5", className)}>
    {children}
  </th>
);
export const Td = ({ children, className }) => (
  <td className={cn("px-4 py-2.5 align-middle", className)}>{children}</td>
);
export const Tr = ({ children, className, ...props }) => (
  <tr className={cn("border-t border-gray-100", className)} {...props}>
    {children}
  </tr>
);

export const EmptyRow = ({ colSpan, children }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-gray-400">
      {children}
    </td>
  </tr>
);

// ── Tag / Badge / Dot ──────────────────────────────────────────────────────
// Neutral, professional tag (type · label + optional actions).
export const Tag = ({ label, sub, actions, title }) => (
  <span
    title={title}
    className="inline-flex items-center rounded-md border border-gray-200 bg-white pl-2 pr-1 py-1 shadow-sm"
  >
    <span className="text-xs font-semibold text-gray-700">{label}</span>
    {sub != null && (
      <>
        <span className="mx-2 h-3.5 w-px bg-gray-200" />
        <span className="text-xs text-gray-500 max-w-[150px] truncate">{sub}</span>
      </>
    )}
    {actions && <span className="ml-1.5 flex items-center">{actions}</span>}
  </span>
);

const DOT = { green: "bg-emerald-500", amber: "bg-amber-500", gray: "bg-gray-300", blue: "bg-blue-500" };
const DOT_TEXT = { green: "text-emerald-600", amber: "text-amber-600", gray: "text-gray-400", blue: "text-blue-600" };
export const StatusDot = ({ tone = "gray", label }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className={cn("h-2 w-2 rounded-full", DOT[tone])} />
    {label && <span className={cn("text-xs font-medium", DOT_TEXT[tone])}>{label}</span>}
  </span>
);

const BADGE_TONE = {
  gray: "bg-gray-100 text-gray-600",
  blue: "bg-blue-50 text-blue-700",
  indigo: "bg-indigo-50 text-indigo-700",
  green: "bg-emerald-50 text-emerald-700",
  amber: "bg-amber-50 text-amber-700",
};
export const Badge = ({ tone = "gray", className, children }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold",
      BADGE_TONE[tone],
      className,
    )}
  >
    {children}
  </span>
);

// Selectable pill (option chips).
export const Chip = ({ active, className, children, ...props }) => (
  <button
    type="button"
    className={cn(
      "px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors",
      active
        ? "border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-200"
        : "border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-600",
      className,
    )}
    {...props}
  >
    {children}
  </button>
);

// ── Toggle switch ──────────────────────────────────────────────────────────
export const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={cn(
      "relative h-5 w-9 shrink-0 rounded-full transition-colors",
      checked ? "bg-blue-600" : "bg-gray-300",
      disabled && "opacity-40",
    )}
  >
    <span
      className={cn(
        "absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all",
        checked ? "left-[18px]" : "left-0.5",
      )}
    />
  </button>
);

export const Spinner = ({ className }) => (
  <span className={cn("h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin", className)} />
);

// ── Chips row for drawer/modal headers (tenant · user type) ────────────────
export const ContextChips = ({ items }) => (
  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
    {items.map((it, i) => (
      <Badge key={i} tone={it.tone || (i === 0 ? "blue" : "gray")}>
        {it.label}
      </Badge>
    ))}
  </div>
);

// ── Modal (centered) ───────────────────────────────────────────────────────
const MODAL_SIZE = { sm: "max-w-md", md: "max-w-lg", lg: "max-w-2xl" };
export const Modal = ({ title, subtitle, headerRight, onClose, footer, size = "md", children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className={cn("w-full bg-white rounded-2xl shadow-xl flex flex-col max-h-[88vh]", MODAL_SIZE[size])}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative px-5 py-4 border-b border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3.5 right-4 flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
          >
            <HiX className="h-4 w-4" />
          </button>
          <div className="flex items-start justify-between gap-4 pr-10">
            <div className="min-w-0">
              {typeof title === "string" ? (
                <h3 className="text-base font-semibold text-gray-900">{title}</h3>
              ) : (
                title
              )}
              {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
            </div>
            {headerRight}
          </div>
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 bg-gray-50/60">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Drawer (right slide-over) ──────────────────────────────────────────────
const DRAWER_WIDTH = { md: "max-w-2xl", lg: "max-w-3xl" };
export const Drawer = ({ title, chips, onClose, footer, width = "lg", children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[1300] flex justify-end bg-black/40" onClick={onClose}>
      <div
        className={cn("h-full w-full bg-gray-50 shadow-2xl flex flex-col", DRAWER_WIDTH[width])}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-6 py-4 bg-white border-b border-gray-200">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 tracking-tight">{title}</h2>
            {chips && <ContextChips items={chips} />}
          </div>
          <IconButton icon={<HiX className="h-5 w-5" />} onClick={onClose} className="p-2 -mr-1" />
        </div>
        {children}
        {footer && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 bg-white border-t border-gray-200">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
